'use client'

import Link from 'next/link'
import type { ContentItem } from '@/lib/types'
import { detailPath, formatRating, typeLabel, cn } from '@/lib/utils'
import { PosterImage } from './PosterImage'
import { FavoriteButton } from './FavoriteButton'

interface MovieCardProps {
  item: ContentItem
  className?: string
  eager?: boolean
}

export function MovieCard({ item, className, eager }: MovieCardProps) {
  return (
    <Link
      href={detailPath(item.type, item.id)}
      className={cn(
        'group relative block overflow-hidden rounded-xl border border-white/5 bg-ink-800 shadow-card transition-all duration-300 hover:-translate-y-1 hover:border-white/15 hover:shadow-glow',
        className
      )}
    >
      <div className="relative">
        <PosterImage src={item.poster} alt={item.title} eager={eager} />

        {/* rating badge */}
        {item.rating ? (
          <div className="absolute left-2 top-2 flex items-center gap-1 rounded-md bg-black/70 px-1.5 py-0.5 text-[11px] font-bold text-amber-300 backdrop-blur">
            <span aria-hidden>★</span>
            {formatRating(item.rating)}
          </div>
        ) : null}

        {/* type badge */}
        <div className="absolute right-2 top-2">
          <span
            className={cn(
              'rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide backdrop-blur',
              item.type === 'series' ? 'bg-sky-500/80 text-white' : 'bg-crimson-600/80 text-white'
            )}
          >
            {typeLabel(item.type)}
          </span>
        </div>

        {/* favorite (hover) */}
        <div className="absolute bottom-2 right-2 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
          <FavoriteButton item={item} />
        </div>

        {/* gradient + info on hover */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 translate-y-2 bg-gradient-to-t from-black/95 via-black/60 to-transparent p-3 opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
          <p className="line-clamp-2 text-sm font-semibold text-white">{item.title}</p>
          <p className="mt-0.5 text-xs text-neutral-300">
            {[item.year, item.genres?.[0]].filter(Boolean).join(' • ')}
          </p>
        </div>
      </div>

      {/* static caption (always visible) */}
      <div className="p-2.5 group-hover:opacity-0 transition-opacity duration-200">
        <p className="line-clamp-1 text-sm font-medium text-neutral-100">{item.title}</p>
        <p className="mt-0.5 text-xs text-neutral-500">{item.year ?? '—'}</p>
      </div>
    </Link>
  )
}
